import { useState, useEffect } from 'react';
import { Button, Icon } from '@/components/atoms';
import { Input } from '@kisanlink/ui-commons';
import { useCreateProductPriceMutation } from '@/hooks/useErpApi';

interface SelectedVariant {
  id: string;
  name: string;
  productName: string;
}

interface MultiVariantPriceFormProps {
  variants: SelectedVariant[];
  onSuccess: () => void;
  onCancel: () => void;
  onBack: () => void;
}

const PRICE_TYPES = [
  { value: 'msp', label: 'msp (maximum selling price)' },
  { value: 'member', label: 'member' },
  { value: 'retail', label: 'retail' },
  { value: 'mrp', label: 'mrp (maximum retail price)' },
];

export function MultiVariantPriceForm({
  variants,
  onSuccess,
  onCancel,
  onBack,
}: MultiVariantPriceFormProps) {
  const [formData, setFormData] = useState({
    price_type: 'retail',
    price: '',
    currency: 'INR',
    effective_from: new Date().toISOString().split('T')[0],
    effective_to: '',
    is_active: true,
  });

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdCount, setCreatedCount] = useState(0);

  const { mutateAsync: createPrice } = useCreateProductPriceMutation();

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const handleChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const validateForm = (): boolean => {
    if (!formData.price || parseFloat(formData.price) <= 0) {
      setError('Price must be greater than 0');
      return false;
    }

    if (!formData.effective_from) {
      setError('Effective from date is required');
      return false;
    }

    if (formData.effective_to && formData.effective_from > formData.effective_to) {
      setError('Effective to date must be after effective from date');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setCreatedCount(0);

    const payload = {
      price_type: formData.price_type,
      price: parseFloat(formData.price),
      currency: formData.currency,
      effective_from: formData.effective_from,
      effective_to: formData.effective_to || undefined,
      is_active: formData.is_active,
    };

    const failed: string[] = [];
    let successCount = 0;

    try {
      // Create prices for all variants
      for (const variant of variants) {
        try {
          await createPrice({
            variant_id: variant.id,
            ...payload,
          });
          successCount++;
          setCreatedCount(successCount);
        } catch (err) {
          failed.push(variant.name);
          // eslint-disable-next-line no-console
          console.error(`Failed to create price for ${variant.name}:`, err);
        }
      }

      if (failed.length === 0) {
        setSuccess(
          `Successfully created prices for all ${variants.length} variant${variants.length > 1 ? 's' : ''}`
        );
        setTimeout(() => {
          onSuccess();
        }, 1500);
      } else if (successCount > 0) {
        setSuccess(
          `Created prices for ${successCount} variant${successCount > 1 ? 's' : ''}. ${failed.length} failed.`
        );
        setError(`Failed variants: ${failed.join(', ')}`);
      } else {
        setError(
          'Failed to create prices for all variants. Please check the errors and try again.'
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while creating prices');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Selected Variants Summary */}
      <div className="bg-neutral-50 rounded-lg p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-neutral-700 mb-2">
              Setting price for {variants.length} variant{variants.length > 1 ? 's' : ''}:
            </p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {variants.map((variant, index) => (
                <div key={variant.id} className="text-sm text-neutral-600">
                  {index + 1}. {variant.name} ({variant.productName})
                </div>
              ))}
            </div>
          </div>
          <Button type="button" variant="secondary" size="sm" onClick={onBack}>
            <Icon name="ArrowLeft" size={14} className="mr-1" />
            Back
          </Button>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-error-50 border border-error-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="CircleAlert" size={20} className="text-error-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-error-900">Error</p>
              <p className="text-sm text-error-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-success-50 border border-success-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="CircleCheck" size={20} className="text-success-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-success-900">Success</p>
              <p className="text-sm text-success-700 mt-1">{success}</p>
            </div>
          </div>
        </div>
      )}

      {/* Progress Indicator */}
      {isSubmitting && (
        <div className="bg-info-50 border border-info-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Icon
              name="LoaderCircle"
              size={20}
              className="text-info-600 animate-spin flex-shrink-0"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-info-900">Creating prices...</p>
              <p className="text-sm text-info-700 mt-1">
                Progress: {createdCount} / {variants.length}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Form Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-neutral-700">
            Price Type <span className="text-error-600">*</span>
          </label>
          <select
            value={formData.price_type}
            onChange={(e) => handleChange('price_type', e.target.value)}
            disabled={isSubmitting}
            required
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-neutral-100"
          >
            {PRICE_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-neutral-500">
            {formData.price_type === 'selling_price' && 'Price customers pay at checkout'}
            {formData.price_type === 'purchase_price' && 'Cost price from supplier'}
            {formData.price_type === 'MRP' && 'Maximum Retail Price (printed on product)'}
          </p>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-neutral-700">
            Price (INR) <span className="text-error-600">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon name="IndianRupee" size={16} className="text-neutral-400" />
            </div>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={formData.price}
              onChange={(e) => handleChange('price', e.target.value)}
              className="pl-10"
              placeholder="0.00"
              disabled={isSubmitting}
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-neutral-700">
            Effective From <span className="text-error-600">*</span>
          </label>
          <Input
            type="date"
            value={formData.effective_from}
            onChange={(e) => handleChange('effective_from', e.target.value)}
            disabled={isSubmitting}
            required
          />
          <p className="text-xs text-neutral-500">Date when this price becomes active</p>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-neutral-700">Effective To</label>
          <Input
            type="date"
            value={formData.effective_to}
            onChange={(e) => handleChange('effective_to', e.target.value)}
            min={formData.effective_from}
            disabled={isSubmitting}
          />
          <p className="text-xs text-neutral-500">Leave empty for no end date</p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="is_active"
          checked={formData.is_active}
          onChange={(e) => handleChange('is_active', e.target.checked)}
          disabled={isSubmitting}
          className="w-4 h-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
        />
        <label htmlFor="is_active" className="text-sm font-medium text-neutral-700">
          Active Price
        </label>
        <p className="text-xs text-neutral-500">
          Only active prices are used in sales and calculations
        </p>
      </div>

      {/* Pricing Guidelines */}
      <div className="bg-info-50 border border-info-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Icon name="Info" size={20} className="text-info-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-info-900">
            <p className="font-medium mb-2">Bulk Pricing Guidelines</p>
            <ul className="space-y-1 text-info-700">
              <li>• This price will be applied to all {variants.length} selected variants</li>
              <li>• Sale Price should typically be between Purchase Price and MRP</li>
              <li>• Only one active price of each type can exist at a time per variant</li>
              <li>
                • Any existing prices with overlapping dates will need to be deactivated first
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-neutral-200">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Icon name="LoaderCircle" size={16} className="mr-2 animate-spin" />
              Creating prices... ({createdCount}/{variants.length})
            </>
          ) : (
            <>
              <Icon name="Save" size={16} className="mr-2" />
              Create Prices for {variants.length} Variant{variants.length > 1 ? 's' : ''}
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
